import type { ReactNode } from 'react';

/** 表格欄位定義。align 只影響對齊，數字欄請一併加 tabular 類。 */
export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  align?: 'left' | 'right';
  className?: string;
}

/** 資料表格。載入／錯誤／空狀態三種情境都統一在這裡處理，
 *  各頁不必各寫一份，行為才會一致。 */
export function DataTable<T>({
  rows,
  columns,
  rowKey,
  loading,
  error,
  empty,
}: {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  loading?: boolean;
  error?: unknown;
  empty?: ReactNode;
}) {
  if (loading) {
    // 骨架屏比一個轉圈好：版面不會跳動，也看得出即將出現什麼
    return (
      <div className="space-y-2 p-6" aria-busy="true" aria-live="polite">
        <span className="sr-only">載入中</span>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="skeleton h-9 w-full" style={{ opacity: 1 - i * 0.11 }} />
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <p className="px-6 py-16 text-center text-[13px] text-destructive">
        載入失敗：{error instanceof Error ? error.message : '未知錯誤'}
      </p>
    );
  }
  if (!rows.length) {
    return (
      <p className="px-6 py-16 text-center text-[13px] text-muted-foreground">
        {empty ?? '目前沒有資料'}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto max-[720px]:overflow-visible">
      <table className="data-table w-full border-collapse">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={c.align === 'right' ? '!text-right' : undefined}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={rowKey(r)}>
              {columns.map((c) => (
                <td
                  key={c.key}
                  // 窄螢幕會把表格轉成卡片，每格用 data-label 當左側標題
                  data-label={typeof c.header === 'string' ? c.header : ''}
                  className={[c.align === 'right' ? 'text-right' : '', c.className ?? '']
                    .filter(Boolean)
                    .join(' ')}
                >
                  {c.cell(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
